import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Card,
  CardContent,
  Grid,
  InputAdornment,
  TextField
} from '@material-ui/core';
import { formatDate } from 'src/utils';
import { KeyboardDatePicker } from '@material-ui/pickers';
import { ChequeImageComponent } from './ChequeImage';
import { PurchaseOrderItem } from 'src/redux/slices/cheque-management/types';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { slices, useAppDispatch } from 'src/redux';
import { unwrapResult } from '@reduxjs/toolkit';
import { PONosTable } from './PoNosTable';
import { Supplier } from 'src/types/supplier';
import { Autocomplete } from '@material-ui/lab';
import { useSnackBar } from 'src/hooks';
import { isValid } from 'date-fns';

const { actions: purchaseOrderActions } = slices.purchaseOrder;
const { actions: supplierActions } = slices.supplier;

interface PurchaseOrderFormProps {
  formikRef?: any;
  grandTotal?: number;
  isEditable?: boolean;
  isForDelivery?: boolean;
  isCreatePage?: boolean;
  currentStatusValue?: string;
}

export const component = ({
  formikRef,
  grandTotal = 0,
  isCreatePage
}: PurchaseOrderFormProps) => {
  const dispatch = useAppDispatch();
  const snackbar = useSnackBar();
  const calendarButtonRef = useRef<HTMLButtonElement>(null);

  const [supplierOptions, setSupplierOptions] = useState<Supplier[]>([]);
  const [addPoInput, setAddPoInput] = useState('');
  const [chequeNo, setChequeNo] = useState('');
  const [isPoInputLoading, setIsPoInputLoading] = useState<boolean>(false);

  const formikRefAsInternal = useMemo(() => formikRef, [formikRef]);

  const selectedPoNos = useMemo(() => formikRef?.values?.po_nos ?? [], [
    formikRef
  ]);

  const selectedSupplier = useMemo(() => {
    const chosenSupplier = supplierOptions?.find(
      (b) => b.id === formikRef?.values.supplier_id
    );
    return chosenSupplier;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplierOptions, formikRef]);

  const getSuppliers = async () => {
    const response = unwrapResult(
      await dispatch(
        supplierActions.getSuppliersThunk({
          keyword: '',
          limit: 9999,
          module: 'purchase-order'
        })
      )
    );
    if (
      response?.success &&
      response?.originalData?.data &&
      response?.originalData?.data?.length > 0
    ) {
      setSupplierOptions(response?.originalData?.data);
    }
  };

  const setPoNos = (updated: PurchaseOrderItem[]) =>
    formikRefAsInternal.setFieldValue('po_nos', updated, true);

  const onAddNewPoNos = async (input: string) => {
    if (!formikRefAsInternal?.values?.supplier_id || input === '') return;
    setIsPoInputLoading(true);

    const newInput = input.trim();
    if (selectedPoNos?.some((po: PurchaseOrderItem) => po.po_no === newInput)) {
      snackbar.show({
        severity: 'error',
        message: 'PO number already added.'
      });
      setIsPoInputLoading(false);
      return;
    }

    const res = unwrapResult(
      await dispatch(
        purchaseOrderActions.getPurchaseOrdersThunk({
          with_out_cheque: 1,
          po_no: input,
          supplier: formikRefAsInternal?.values?.supplier_id
        })
      )
    );

    // do not accept if still loading
    if (res.success) {
      const poOptions = res.originalData.data ?? [];
      const matchedOption = poOptions.find((p) => p.order_no === newInput);

      if (matchedOption) {
        let newPo = {
          po_no: newInput,
          dr_no: matchedOption?.dr_no ?? '',
          terms: matchedOption?.terms ?? '',
          amount: parseFloat(
            matchedOption?.amount?.replace(/[^\d.]/g, '') ?? ''
          )
        };

        setPoNos([...selectedPoNos, newPo]);
        setAddPoInput('');
      } else {
        snackbar.show({
          severity: 'error',
          message: 'PO number not found. Please check for typos.'
        });
      }
    } else {
      snackbar.show({
        severity: 'error',
        message: res.message ?? 'Failed to add new po no. Please contact devs'
      });
    }

    setIsPoInputLoading(false);
  };

  const onDeletePo = (index: number) => {
    const updated = [...(formikRefAsInternal?.values?.po_nos ?? [])];
    updated.splice(index, 1);
    formikRefAsInternal.setFieldValue('po_nos', updated, true);
  };

  // TODO: Retain temporarily for easy revert
  // const onSearchPOWithoutCheque = useCallback(
  //   async (search = '') => {
  //     try {
  //       if (!search) {
  //         return;
  //       }

  //       if (!formikRefAsInternal?.values?.supplier_id) {
  //         snackbar.show({
  //           severity: 'warning',
  //           message: 'Please select a supplier first.'
  //         });
  //         return;
  //       }

  //       const res = unwrapResult(
  //         await dispatch(
  //           purchaseOrderActions.getPurchaseOrdersThunk({
  //             with_out_cheque: 1,
  //             po_no: search,
  //             supplier: formikRefAsInternal?.values?.supplier_id
  //           })
  //         )
  //       );

  //       if (
  //         res?.success &&
  //         res?.originalData?.data &&
  //         res?.originalData?.data.length <= 0
  //       ) {
  //         snackbar.show({
  //           severity: 'warning',
  //           message: 'This PO number does not exist'
  //         });
  //       }

  //       const poList = Array.isArray(res.originalData.data)
  //         ? res.originalData.data.map((po: any) => ({
  //             po_no: po.order_no,
  //             amount: po.amount
  //               .replace(/[^\d.]/g, '')
  //               .replace(/(\.\d*)\./g, '$1'),
  //             terms: po.terms ?? ''
  //           }))
  //         : [];

  //       setPoOptions(poList);
  //     } catch (e) {
  //       console.error(e);
  //     }
  //   },
  //   [dispatch, formikRefAsInternal.values.supplier_id, snackbar]
  // );

  const handleAmountChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    type?: string
  ) => {
    const numericValue = e.target.value.replace(/[^0-9.]/g, '');
    if (type === 'credit_memo_amount') {
      formikRefAsInternal.setFieldValue('credit_memo_amount', numericValue);
    } else {
      formikRefAsInternal.setFieldValue('amount', numericValue);
    }
  };

  useEffect(() => {
    if (formikRef.values.cheque_no) {
      setChequeNo(formikRef.values.cheque_no ?? '');
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formikRef]);

  useEffect(() => {
    getSuppliers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <form>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            autoComplete="off"
            label="Cheque No."
            placeholder="[BANK NAME]-POs-TS-AGN[TERMS]D"
            variant="outlined"
            value={chequeNo}
            onBlur={() =>
              formikRefAsInternal?.setFieldValue('cheque_no', chequeNo, true)
            }
            onChange={(e: any) => setChequeNo(e.target.value)}
            onKeyDown={(e: any) => {
              if (e.key === 'Enter') {
                formikRefAsInternal?.setFieldValue('cheque_no', chequeNo, true);
              }
            }}
            name={`cheque_no`}
            helperText={
              formikRefAsInternal?.touched.cheque_no &&
              formikRefAsInternal?.errors.cheque_no
            }
            error={Boolean(
              formikRefAsInternal?.touched.cheque_no &&
                formikRefAsInternal?.errors.cheque_no
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <KeyboardDatePicker
            fullWidth
            autoComplete="off"
            format="MMMM dd, yyyy"
            inputVariant="outlined"
            variant="inline"
            name="pdc_date"
            id="pdc_date"
            label="PDC Date"
            autoOk
            value={formikRefAsInternal?.values.pdc_date || null}
            onChange={(value) => {
              if (value && isValid(new Date(value))) {
                formikRefAsInternal?.setFieldValue(
                  'pdc_date',
                  formatDate(value, 'yyyy-MM-dd'),
                  true
                );
              }
            }}
            onBlur={formikRefAsInternal?.handleBlur}
            helperText={
              formikRefAsInternal?.touched.pdc_date &&
              formikRefAsInternal?.errors.pdc_date
            }
            error={Boolean(
              formikRefAsInternal?.touched.pdc_date &&
                formikRefAsInternal?.errors.pdc_date
            )}
            KeyboardButtonProps={{
              'aria-label': 'change date',
              ref: calendarButtonRef
            }}
            InputProps={{
              readOnly: true,
              onClick: () => {
                calendarButtonRef.current?.click();
              }
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            variant="outlined"
            autoComplete="off"
            label="Amount"
            name="amount"
            value={formikRefAsInternal?.values?.amount}
            onBlur={formikRefAsInternal?.handleBlur}
            onChange={handleAmountChange}
            helperText={
              formikRefAsInternal?.touched.amount &&
              formikRefAsInternal?.errors.amount
            }
            error={Boolean(
              formikRefAsInternal?.touched.amount &&
                formikRefAsInternal?.errors.amount
            )}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">₱</InputAdornment>
              ),
              inputProps: {
                step: '0.01',
                min: 0,
                pattern: '[0-9.]*'
              }
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            variant="outlined"
            autoComplete="off"
            label="Credit Memo Amount"
            name="credit_memo_amount"
            defaultValue={formikRefAsInternal?.values?.credit_memo_amount || 0}
            value={formikRefAsInternal?.values?.credit_memo_amount}
            onBlur={formikRefAsInternal?.handleBlur}
            onChange={(e: any) => handleAmountChange(e, 'credit_memo_amount')}
            helperText={
              formikRefAsInternal?.touched.credit_memo_amount &&
              formikRefAsInternal?.errors.credit_memo_amount
            }
            error={Boolean(
              formikRefAsInternal?.touched.credit_memo_amount &&
                formikRefAsInternal?.errors.credit_memo_amount
            )}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">₱</InputAdornment>
              ),
              inputProps: {
                step: '0.01',
                min: 0,
                pattern: '[0-9.]*'
              }
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Autocomplete
            id="supplier_id"
            options={supplierOptions}
            fullWidth
            // cannot change supplier while po are assigned
            disabled={formikRef?.values?.po_nos?.[0]}
            getOptionLabel={(option) => option.name || ''}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Supplier"
                variant="outlined"
                autoComplete="off"
                helperText={
                  !formikRefAsInternal?.values.supplier_id &&
                  formikRefAsInternal?.touched.supplier_id &&
                  'Supplier is required'
                }
                error={Boolean(
                  formikRefAsInternal?.touched.supplier_id &&
                    formikRefAsInternal?.errors.supplier_id
                )}
              />
            )}
            value={selectedSupplier || null}
            onBlur={formikRefAsInternal?.handleBlur}
            onChange={(_, val) => {
              formikRefAsInternal?.setFieldValue('supplier_id', val?.id, true);
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardContent>
              <PerfectScrollbar>
                <PONosTable
                  grandTotal={grandTotal}
                  isPoInputLoading={isPoInputLoading}
                  poNos={formikRefAsInternal?.values?.po_nos ?? []}
                  onRemove={onDeletePo}
                  supplierId={formikRefAsInternal.values.supplier_id}
                  onAddNewPoNos={onAddNewPoNos}
                  addPoInput={addPoInput}
                  setAddPoInput={(val) => setAddPoInput(val)}
                />
              </PerfectScrollbar>
            </CardContent>
          </Card>
        </Grid>
        {isCreatePage ? null : (
          <Grid item xs={12} sm={12}>
            <ChequeImageComponent
              formikRef={formikRefAsInternal}
              isCreatePage={isCreatePage}
            />
          </Grid>
        )}
      </Grid>
    </form>
  );
};

export const ChequeForm = React.memo(component);
